import { getIn } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { Button, Col, Row } from 'reactstrap'
import { formatAgentOptions } from '../../../../api/agents/formatters'
import agents from '../../../../api/agents/hooks'
import { ApplicationLanguage } from '../../../../api/globalTypes'
import { OfferEmailSettingsGroup, OfferTemplate } from '../../../../api/offers/types'
import settings from '../../../../api/settings/hooks'
import { MODAL_TYPES } from '../../../../common/constants'
import { offerModalFields } from '../../../../common/forms/generalRealEstate/fields'
import { showModal } from '../../../../store/modal'
import { Field, FieldGroup } from '../../../../utils/formUtils/types'
import RecursiveForm from '../../../RecursiveForm'
import { OfferBaseTabProps } from '../types'
import { generateOfferSettingsGroup } from '../utils'
import OfferContactDisplay from './OfferContactDisplay'

const OfferBaseTab = (props: OfferBaseTabProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { activeTab, formik, inputs, items, offerTemplates, offerEmailSettingsGroup } = props
    const { OFFER_TEMPLATE, CONTACT_ID, CONTACT_NAME, CONTACT_EMAIL, CONTACT_COMPANY_COLLECTION } = offerModalFields
    const contactFields = [CONTACT_ID, CONTACT_NAME, CONTACT_EMAIL, CONTACT_COMPANY_COLLECTION]

    const selectedTemplate = getIn(formik?.values?.offer, OFFER_TEMPLATE)
    const selectedContacts = getIn(formik?.values?.offer, 'offer_email_settings_group') as OfferEmailSettingsGroup[]

    const [cascadeKey, setCascadeKey] = useState(0)

    const { data: agentsData } = agents.GetAgents.useQuery()
    const { data: availableLanguages } = settings.GetApplicationSettings.useQuery({
        select: (data) => data.availableLanguages,
    })

    const selectedTemplateData = offerTemplates?.items.find((template) => template.id === selectedTemplate)
    const showPrintSettings = selectedTemplateData?.additionalOptions ?? false

    // modify inputs to add dynamic key to reset ContactCompanyCascadeInput on handleContactAdd
    const formattedInputs = inputs.map((input: Field | FieldGroup) =>
        input.name === offerModalFields.CONTACT_COMPANY ? { ...input, name: `${input.name}_${cascadeKey}` } : input
    )

    const selectOptions = {
        [offerModalFields.OFFER_LOCALE]: (availableLanguages ?? []).map((lang: ApplicationLanguage) => ({
            value: lang.abbreviation,
            label: lang.abbreviation.toUpperCase(),
        })),
        [offerModalFields.AGENT_ID]: formatAgentOptions(agentsData),
        // Added only from contact profile
        // ContactCompanyCascadeInput receives fieldNames prefixed with the subform
        ...(offerEmailSettingsGroup && {
            [`offer.${offerModalFields.CONTACT_ID}`]: offerEmailSettingsGroup.contact_id
                ? [
                      {
                          value: String(offerEmailSettingsGroup.contact_id),
                          label: [
                              offerEmailSettingsGroup.internalAppId ? `ID: ${offerEmailSettingsGroup.internalAppId} -` : null,
                              offerEmailSettingsGroup.contact_name,
                              offerEmailSettingsGroup.contact_email || null,
                          ]
                              .filter(Boolean)
                              .join(' '),
                      },
                  ]
                : [],
            [`offer.${offerModalFields.CONTACT_COMPANY_COLLECTION}`]: offerEmailSettingsGroup.contact_company_collection.map((c) => ({
                value: String(c.contact_id),
                label: [c.internalAppId ? `ID: ${c.internalAppId} -` : null, c.contact_name].filter(Boolean).join(' '),
            })),
        }),
    }

    const contactData = contactFields.reduce((acc, curr) => {
        acc = {
            ...acc,
            [curr]: getIn(formik?.values?.offer, curr),
        }
        return acc
    }, {} as OfferEmailSettingsGroup)

    const handleTemplateSelect = (templateId: number) => {
        formik?.setFieldValue('offer.offer_template', templateId)
    }

    const handleContactAdd = () => {
        if (!contactData.contact_email) {
            toast.error(t('modal.offerBase.message.missingEmail') as string)
            return
        }

        if (selectedContacts.some((item) => item.contact_email === contactData.contact_email)) {
            toast.error(t('modal.offerBase.message.duplicateContact') as string)
            return
        }

        // set contact data to offer_email_settings_group formik field - used for OfferSendModal & OfferContactDisplay
        const currentEmailSettings = getIn(formik?.values?.offer, 'offer_email_settings_group') || []
        const updatedEmailSettings = [...currentEmailSettings, contactData]
        formik.setFieldValue('offer.offer_email_settings_group', updatedEmailSettings)

        // clear contact data from OfferBaseModal fields
        contactFields.forEach((field) => formik.setFieldValue(`offer.${field}`, field === 'contact_company_collection' ? [] : ''))
        setCascadeKey((key) => key + 1)
    }

    const handleContactRemove = (contactId: string | number, contactEmail: string) => {
        const currentEmailSettings = getIn(formik?.values?.offer, 'offer_email_settings_group') || []
        const updatedEmailSettings = currentEmailSettings.filter(
            (contact: OfferEmailSettingsGroup) => !(contact.contact_id === contactId && contact.contact_email === contactEmail)
        )

        formik.setFieldValue('offer.offer_email_settings_group', updatedEmailSettings)
    }

    const handleOfferSettings = () => {
        const currentOfferSettingsGroup = getIn(formik?.values?.offer, 'offer_settings_group') || []

        const offerSettingsGroup =
            currentOfferSettingsGroup.length > 0 ? currentOfferSettingsGroup : generateOfferSettingsGroup(currentOfferSettingsGroup, items || [])

        if (currentOfferSettingsGroup.length === 0 && offerSettingsGroup.length > 0) {
            formik.setFieldValue('offer.offer_settings_group', offerSettingsGroup)
        }

        dispatch(
            showModal({
                modalType: MODAL_TYPES.OFFER_SETTINGS_MODAL,
                modalProps: {
                    items: items || [],
                    offerSettingsGroup,
                    offerLocale: getIn(formik?.values?.offer, 'offer_locale'),
                    offerTemplate: getIn(formik?.values?.offer, 'offer_template'),
                    agentId: getIn(formik?.values?.offer, 'agent_id'),
                },
            })
        )
    }

    return (
        <>
            <RecursiveForm
                subform={'offer'}
                formik={formik}
                inputs={formattedInputs}
                selectOptions={selectOptions}
                selectOptionsLoading={undefined}
                config={undefined}
                inputComponents={undefined}
                fieldArrayDeleteComponent={undefined}
            />
            {activeTab === 'offer' && (
                <Button color="none" className="text-edit p-0 mb-4" onClick={handleContactAdd}>
                    <i className="mdi mdi-plus-circle fs-17"></i> {t('button.add')}
                </Button>
            )}
            <Row>
                <Col sm={12}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="fw-medium mb-0">{t('app.common.template')}</h6>
                        {showPrintSettings && (
                            <Button color="light" onClick={handleOfferSettings}>
                                <i className="mdi mdi-cog"></i>
                            </Button>
                        )}
                    </div>
                </Col>
                {offerTemplates?.items.map((template: OfferTemplate) => (
                    <Col sm={4} key={template.id}>
                        <img
                            onClick={() => handleTemplateSelect(template.id)}
                            className={`image-template ${template.id === selectedTemplate ? 'image-selected' : ''}`}
                            src={template.cover}
                        />
                    </Col>
                ))}
            </Row>
            {selectedContacts.length > 0 && activeTab === 'offer' && (
                <OfferContactDisplay selectedContacts={selectedContacts} items={items} handleContactRemove={handleContactRemove} />
            )}
        </>
    )
}

export default OfferBaseTab
